/**
 * As regras PURAS da moldura da ficha e das abas da frente A2: qual aba aparece e em que ordem, o
 * radar de estilo raiz, o próximo produto sugerido e as contas da vigência.
 *
 * Cada função é o porte literal da original do legado, com o `admin.html:linha` ao lado. Sem banco,
 * sem tela e sem relógio implícito: quem precisa de "hoje" recebe a data por parâmetro.
 */

// O catálogo das abas e a tradução de produto do lead para chave de parâmetro.
import { ABAS_DA_FICHA, LEAD_PRODUTO_TO_PARAM, ORDEM_JORNADA, type AbaDaFicha, type IdDaAba } from './catalogo.js';
// Os estilos do radar e as opções da pergunta 23 moram na cópia única da ficha (`../constantes.ts`).
import { ESTILOS_RAIZ, OPCOES_Q23 } from '../constantes.js';

/* ===========================================================================
   AS ABAS DA BARRA (`aplicarVisibilidadeAbas`, `admin.html:6086-6145`)
   =========================================================================== */

/** As chaves de parâmetro dos produtos do lead, sem repetição e sem produto desconhecido. */
export function chavesDeParametro(produtos: readonly string[]): string[] {
  // `filter(Boolean)` do legado: produto sem tradução some da lista.
  return [...new Set(produtos.map((p) => LEAD_PRODUTO_TO_PARAM[p]).filter((c): c is string => Boolean(c)))];
}

/** A ordem dos 16 botões no HTML do legado (`admin.html:2279-2294`), que é a de quem acabou de carregar a página. */
export const ORDEM_DO_HTML: readonly IdDaAba[] = Object.freeze(ABAS_DA_FICHA.map((aba) => aba.id));

/**
 * A barra montada.
 *
 * O botão ACESO e o conteúdo MOSTRADO são dois campos, e não um, porque no legado eles podem
 * divergir (ver o defeito reproduzido em `montarBarraDeAbas`).
 */
export interface BarraDeAbas {
  // As abas visíveis, na ordem final da barra.
  readonly visiveis: readonly AbaDaFicha[];
  // A ordem nova dos 16 botões, escondidos inclusive: é a que a PRÓXIMA ficha herda.
  readonly ordem: readonly IdDaAba[];
  // O botão que fica aceso.
  readonly acesa: IdDaAba;
  // O conteúdo que fica na tela.
  readonly conteudo: IdDaAba;
  /**
   * A aba em que o legado CLICA quando o botão aceso ficou escondido (`primeiroVisivel.click()`,
   * `admin.html:6138`), ou nula quando ele não clica em nada.
   *
   * Ela existe separada de `acesa` porque o clique do legado não troca a aba na hora: ele roda o
   * carregador dela primeiro e só então acende e mostra (`5086-5091`). Quem monta a barra precisa
   * saber a diferença entre "já está assim" e "o legado clicaria aqui agora".
   */
  readonly clique: IdDaAba | null;
}

/**
 * Decide quais abas aparecem, em que ordem, qual botão acende e qual conteúdo aparece.
 *
 * `linhas` é o que `parametros_produto_aba` devolveu para os produtos do lead (ou vazio, se a
 * leitura falhou, como o `data || []` do legado). Três regras do legado, e as três ficam aqui:
 *   · sem produto vinculado, todas as abas aparecem (comentário de `admin.html:6106-6109`);
 *   · aba sem chave de parâmetro (o Histórico) aparece sempre;
 *   · a ordem vem de "Vínculo Produto X Aba"; empate de aba em dois produtos fica com a MENOR ordem,
 *     e aba sem ordem vai para o fim, na posição relativa que tinha ANTES desta ficha.
 *
 * `ordemAtual` é a ordem dos botões como a ficha ANTERIOR os deixou (`ORDEM_DO_HTML` na primeira
 * ficha depois de carregar a página).
 *
 * DEFEITO DO LEGADO, REPRODUZIDO IGUAL (decisão do fundador: defeito do legado se reproduz, isolado
 * num ponto só e com o achado citado; achado 1 da verificação independente da A2, `progresso/
 * VERIFICACAO-A2.md`). O legado reordena os BOTÕES no DOM com `container.appendChild(btn)`
 * (`admin.html:6136`), e o DOM não volta à ordem do HTML ao abrir outra cliente. Três efeitos saem
 * disso, e os três ficam só aqui:
 *   1. A ordem da barra PASSA para a próxima ficha. O desempate "posição relativa original" usa o
 *      índice na ordem ATUAL do DOM (`6128`), então uma cliente sem produto (tudo sem ordem)
 *      herda a barra da cliente anterior.
 *   2. O `resetarAbas` acende o botão de índice 0 da ordem ATUAL, mas mostra o conteúdo de índice
 *      0, que é SEMPRE o Questionário Geral (`5107-5110`, conteúdos em `2297`). Depois de uma
 *      cliente cuja ordem pôs Mensagens primeiro, a próxima abre com o botão Mensagens aceso e o
 *      Questionário na tela.
 *   3. Se o botão aceso ficou escondido, o legado clica no primeiro visível da ordem ATUAL, ainda
 *      antes de reordenar (`primeiroVisivel`, `6116-6122`), e aí botão e conteúdo voltam a bater.
 */
/**
 * `true` reproduz o botão aceso que DIVERGE do conteúdo: o legado acende o de índice 0 da ordem
 * atual (`admin.html:5108`) e desenha o Questionário Geral (`5109`), e os dois só coincidem quando
 * a ordem daquele cliente põe o Questionário em primeiro.
 *
 * **PASSOU A SER `false` EM 07/10/2026, POR DECISÃO DO FUNDADOR**, depois de ele abrir a ficha de
 * um cliente, ver "E-mail Atendimento" aceso e o Questionário Geral na tela, e escrever: *"está
 * marcando uma coisa e mostrando outra"*.
 *
 * A CHAVE FICA, como a de `pessoas-indicacao.tsx`: ela é o registro de que isto foi uma escolha, e
 * é o que permite comparar com o admin atual quando alguém perguntar por que os dois diferem.
 */
const REPRODUZIR_ACESO_DIVERGENTE = false;

export function montarBarraDeAbas(
  produtos: readonly string[],
  linhas: readonly { readonly aba: string; readonly ordem: number | null }[],
  ordemAtual: readonly IdDaAba[] = ORDEM_DO_HTML,
  // O botão aceso e o conteúdo na hora em que os vínculos chegam. Sem eles, os do `resetarAbas`;
  // com eles, vale o que a pessoa tocou durante a carga, como a classe `aba-ativa` do legado.
  agora?: { readonly acesa: IdDaAba; readonly conteudo: IdDaAba },
): BarraDeAbas {
  const chaves = chavesDeParametro(produtos);
  // As abas habilitadas e a menor ordem de cada uma.
  const habilitadas = new Set<string>();
  const ordemPorAba = new Map<string, number | null>();
  // O legado só consulta quando há chave; sem chave, as linhas não existem.
  if (chaves.length > 0) {
    for (const r of linhas) {
      habilitadas.add(r.aba);
      const atual = ordemPorAba.get(r.aba);
      // `Number()` reproduz a comparação do legado com `null`, que vale 0 em `<`.
      if (!ordemPorAba.has(r.aba) || Number(r.ordem) < Number(atual)) ordemPorAba.set(r.aba, r.ordem);
    }
  }
  const semProdutoVinculado = chaves.length === 0;
  // Visível: sem produto, ou sem chave, ou com a chave habilitada.
  const visivel = (aba: AbaDaFicha) => semProdutoVinculado || !aba.param || habilitadas.has(aba.param);

  // Os botões na ordem em que a ficha anterior os deixou (`Array.from(container.querySelectorAll)`).
  const botoes = botoesNaOrdem(ordemAtual);
  // O primeiro visível na ordem ATUAL, antes de reordenar: é o que o legado clica (`6121`).
  const primeiroVisivel = botoes.find(visivel);
  // O botão aceso agora: o que a pessoa tocou durante a carga, ou o que o `resetarAbas` acendeu,
  // o de índice 0 da ordem ATUAL (`5108`).
  const acesoAgora = botoes.find((aba) => aba.id === agora?.acesa) ?? botoes[0]!;
  // O conteúdo agora: o tocado, ou o de índice 0, que é sempre o Questionário Geral (`5109`).
  const conteudoAgora: IdDaAba = agora?.conteudo ?? 'aba-questionario';
  // O legado só troca a aba quando o botão aceso ficou escondido (`ativoOculto`, `6123` e `6138`).
  const trocaPeloOculto = !visivel(acesoAgora) && primeiroVisivel !== undefined;
  // Trocou: botão e conteúdo são os do primeiro visível. Não trocou: ficam os de agora.
  const conteudo: IdDaAba = trocaPeloOculto ? primeiroVisivel.id : conteudoAgora;
  /**
   * O BOTÃO ACESO É O DO CONTEÚDO QUE ESTÁ NA TELA. Sempre.
   *
   * O DEFEITO DO LEGADO QUE ISTO CORRIGE, apontado pelo fundador em 07/10/2026 com a captura da
   * ficha: *"quando a admin seleciona um cliente, esta marcando uma coisa e mostrando outra...
   * Mostra como default a aba Email mas mostra outra coisa"*. Na captura dele, o botão aceso era
   * "E-mail Atendimento" e o conteúdo era o Questionário Geral.
   *
   * POR QUE O LEGADO FAZ ISSO: ao abrir a ficha, o `resetarAbas` acende o botão de ÍNDICE 0 da
   * ordem atual (`admin.html:5108`), e a ordem muda de cliente para cliente, porque cada produto
   * reordena as abas. O conteúdo, por outro lado, é sempre o Questionário Geral (`5109`). Quando a
   * ordem daquele cliente põe outra aba em primeiro, os dois deixam de bater.
   *
   * REPRODUZIR ISSO ERA O COMBINADO ATÉ HOJE, e ele revogou. O conteúdo continua sendo o mesmo que
   * o legado mostra: o que muda é o botão, que passa a apontar para o que está na tela em vez de
   * apontar para outra coisa. Interface que marca A e mostra B não é fidelidade: é a pessoa
   * clicando na aba certa e achando que o sistema não respondeu.
   */
  const acesa: IdDaAba = REPRODUZIR_ACESO_DIVERGENTE
    ? trocaPeloOculto
      ? primeiroVisivel.id
      : acesoAgora.id
    : conteudo;

  // A reordenação dos 16 botões (`admin.html:6128-6136`), com o índice NA ORDEM ATUAL de desempate.
  const ordenadas = botoes.map((aba, indiceOriginal) => ({ aba, indiceOriginal })).sort((a, b) => {
    // Aba sem chave, ou sem linha, não tem ordem (o `undefined` do legado).
    const ordemA = a.aba.param !== null && ordemPorAba.has(a.aba.param) ? ordemPorAba.get(a.aba.param) : undefined;
    const ordemB = b.aba.param !== null && ordemPorAba.has(b.aba.param) ? ordemPorAba.get(b.aba.param) : undefined;
    if (ordemA === undefined && ordemB === undefined) return a.indiceOriginal - b.indiceOriginal;
    if (ordemA === undefined) return 1;
    if (ordemB === undefined) return -1;
    // `null - n` no legado vale `0 - n`; `Number(null)` é 0, e o resultado é o mesmo.
    return Number(ordemA) - Number(ordemB);
  });
  // A ordem final vale para todos os botões, os escondidos inclusive, como o `appendChild` de todos.
  const ordem = ordenadas.map((o) => o.aba);
  return {
    visiveis: ordem.filter(visivel),
    ordem: ordem.map((aba) => aba.id),
    acesa,
    conteudo,
    // O legado só clica quando o aceso ficou escondido; nos outros casos ele não toca na aba.
    clique: trocaPeloOculto ? primeiroVisivel.id : null,
  };
}

/**
 * A barra ENQUANTO a ficha carrega, antes de os vínculos chegarem.
 *
 * No legado a tela da ficha aparece logo depois do último acesso (`mostrar('tela-cliente')`,
 * `admin.html:5951`), e a barra só é refeita no fim da carga (`aplicarVisibilidadeAbas`, `6020`).
 * Entre os dois, ela é a que a ficha anterior deixou: a mesma ordem, os mesmos botões escondidos, o
 * botão de índice 0 aceso pelo `resetarAbas` e o Questionário Geral na tela (`5107-5110`). Na
 * primeira ficha da página, o HTML: os 16 botões, com o Questionário. Mesmo defeito reproduzido de
 * `montarBarraDeAbas`, com o mesmo achado.
 *
 * `visiveisDaAnterior` nulo quer dizer que nenhuma ficha escondeu botão ainda.
 */
export function barraEnquantoCarrega(ordemAtual: readonly IdDaAba[], visiveisDaAnterior: readonly IdDaAba[] | null): BarraDeAbas {
  const botoes = botoesNaOrdem(ordemAtual);
  // Os botões que a ficha anterior deixou à mostra, na ordem que ela deixou.
  const visiveis = visiveisDaAnterior === null ? botoes : botoes.filter((aba) => visiveisDaAnterior.includes(aba.id));
  // Nada é clicado aqui: o `resetarAbas` só troca classe, sem chamar carregador nenhum (`5107-5110`).
  // O botão aceso acompanha o conteúdo, pelo mesmo motivo de `montarBarraDeAbas`: o legado acende
  // o de índice 0 e desenha o Questionário, e os dois podem não ser o mesmo.
  const acesaEnquantoCarrega: IdDaAba = REPRODUZIR_ACESO_DIVERGENTE ? botoes[0]!.id : 'aba-questionario';
  return {
    visiveis,
    ordem: botoes.map((aba) => aba.id),
    acesa: acesaEnquantoCarrega,
    conteudo: 'aba-questionario',
    clique: null,
  };
}

/**
 * As abas do catálogo na ordem pedida.
 *
 * Defesa de quem guarda a ordem fora daqui: id desconhecido é ignorado, e aba que faltar entra no
 * fim, na ordem do HTML. Sem isso, uma ordem guardada incompleta sumiria com um botão da barra.
 */
function botoesNaOrdem(ordem: readonly IdDaAba[]): AbaDaFicha[] {
  // O catálogo por id, para achar cada aba da ordem pedida.
  const porId = new Map(ABAS_DA_FICHA.map((aba) => [aba.id, aba] as const));
  // As abas da ordem pedida, sem repetição e sem id desconhecido.
  const vistas = new Set<IdDaAba>();
  const lista: AbaDaFicha[] = [];
  for (const id of ordem) {
    const aba = porId.get(id);
    if (aba && !vistas.has(id)) {
      vistas.add(id);
      lista.push(aba);
    }
  }
  // As que faltaram, no fim e na ordem do HTML.
  for (const aba of ABAS_DA_FICHA) if (!vistas.has(aba.id)) lista.push(aba);
  return lista;
}

/* ===========================================================================
   AS LISTAS DE MARCAR, como o legado as lê
   =========================================================================== */

/**
 * `valores.includes(opcao)`, com o `|| []` do legado na frente (`admin.html:4833` e `4926`).
 *
 * O valor pode ser lista ou texto (a coluna do Questionário Geral é `text` no banco de
 * homologação), e o legado chama `includes` nos dois sem distinguir: em lista compara item a
 * item, em texto procura o trecho. Reproduzir exatamente isso é o que mantém a marca igual.
 */
export function contem(valores: readonly string[] | string | null | undefined, opcao: string): boolean {
  // Falso, nulo ou texto vazio viram lista vazia, como o `|| []`.
  const lista = valores || [];
  // Lista e texto têm `includes`, e cada um faz o que o legado fazia.
  return typeof lista === 'string' ? lista.includes(opcao) : lista.includes(opcao);
}

/* ===========================================================================
   O RADAR DE ESTILO RAIZ (`admin.html:4922-5018`)
   =========================================================================== */

/** A pontuação de um estilo no radar. */
export interface PontoRaiz {
  readonly estilo: string;
  readonly pontuacao: number;
}

/** Uma linha qualquer, lida por nome de coluna, como o legado lê `geral?.[campo]`. */
type LinhaPorNome = Readonly<Record<string, unknown>> | null | undefined;

// A quantidade de marcas de uma coluna da Personalidade, como `(personalidade?.[coluna] || []).length`.
function tamanhoDe(valor: unknown): number {
  // Lista e texto têm `length`; qualquer outra coisa (nulo, número) vale zero, como o `|| []`.
  if (Array.isArray(valor) || typeof valor === 'string') return valor.length;
  return 0;
}

/**
 * A pontuação final de cada estilo (`calcularPontosRaiz`, `admin.html:4922-4933`): o número de
 * marcas na coluna da Personalidade, mais um ponto por pergunta 23 cuja opção da mesma posição foi
 * marcada no Questionário Geral.
 */
export function calcularPontosRaiz(geral: LinhaPorNome, personalidade: LinhaPorNome): PontoRaiz[] {
  return ESTILOS_RAIZ.map((item, indice) => {
    const base = tamanhoDe(personalidade?.[item.coluna]);
    let incremento = 0;
    for (const campo of Object.keys(OPCOES_Q23)) {
      const opcao = OPCOES_Q23[campo]![indice]!;
      // A coluna do Geral pode ser lista ou texto; `contem` reproduz o `includes` do legado.
      const marcados = geral?.[campo] as readonly string[] | string | null | undefined;
      if (contem(marcados, opcao)) incremento += 1;
    }
    return { estilo: item.estilo, pontuacao: base + incremento };
  });
}

/** A geometria do radar em coordenadas do `viewBox` 0 0 300 300 (`construirSvgRadar`, `4935-4976`). */
export interface GeometriaDoRadar {
  // As cinco grades, da menor para a maior; só a última é contínua.
  readonly grades: readonly string[];
  // Os sete eixos, do centro à borda.
  readonly eixos: readonly { readonly x: number; readonly y: number }[];
  // O polígono das pontuações.
  readonly dados: string;
  // Os rótulos dos estilos, já com o deslocamento vertical do legado.
  readonly rotulos: readonly { readonly x: number; readonly y: number; readonly texto: string }[];
}

/** Monta a geometria do radar com as mesmas contas do legado. */
export function geometriaDoRadar(pontos: readonly PontoRaiz[]): GeometriaDoRadar {
  const n = pontos.length;
  // Centro e raio máximo do desenho do legado, em unidades do `viewBox`.
  const centro = 150;
  const raioMax = 105;
  // A escala vai a pelo menos 10 e sobe de 2 em 2 acima do maior valor.
  const maiorValor = Math.max(...pontos.map((p) => p.pontuacao), 1);
  const escalaMax = Math.max(10, Math.ceil(maiorValor / 2) * 2);
  // O ponto de um eixo a uma distância do centro; o primeiro eixo aponta para cima.
  const pontoEm = (indice: number, raio: number): [number, number] => {
    const angulo = (Math.PI * 2 * indice) / n - Math.PI / 2;
    return [centro + raio * Math.cos(angulo), centro + raio * Math.sin(angulo)];
  };
  // O ponto de um valor na escala.
  const pontoValor = (indice: number, valor: number) => pontoEm(indice, (valor / escalaMax) * raioMax);
  // Cinco grades concêntricas.
  const passos = 5;
  const grades: string[] = [];
  for (let g = 1; g <= passos; g++) {
    const valorGrade = (escalaMax / passos) * g;
    grades.push(pontos.map((_, i) => pontoValor(i, valorGrade).join(',')).join(' '));
  }
  // Os eixos, até o raio máximo.
  const eixos = pontos.map((_, i) => {
    const [x, y] = pontoEm(i, raioMax);
    return { x, y };
  });
  // O polígono das pontuações.
  const dados = pontos.map((p, i) => pontoValor(i, p.pontuacao).join(',')).join(' ');
  // Os rótulos, 22 unidades além da borda, com o ajuste vertical de cima, de baixo e do meio.
  const rotulos = pontos.map((p, i) => {
    const [x, y] = pontoEm(i, raioMax + 22);
    const deslocY = y < centro - 5 ? -2 : y > centro + 5 ? 10 : 4;
    return { x, y: y + deslocY, texto: p.estilo };
  });
  return { grades, eixos, dados, rotulos };
}

/* ===========================================================================
   A JORNADA, O PRÓXIMO PRODUTO E A VIGÊNCIA
   =========================================================================== */

/** O índice da etapa atual; etapa desconhecida vale a primeira (`Math.max(0, indexOf)`, `5621`). */
export function indiceDaEtapa(etapa: string): number {
  return Math.max(0, (ORDEM_JORNADA as readonly string[]).indexOf(etapa));
}

/**
 * O primeiro produto do roteiro que o lead ainda não tem (`calcularProximoProdutoSugerido`,
 * `admin.html:9524-9530`). Ordem nula vai para o fim, como o `?? 999` do legado.
 */
export function proximoProdutoSugerido(
  roteiro: readonly { readonly produto: string; readonly ordem: number | null }[],
  produtosDoLead: readonly string[],
): string | null {
  if (!roteiro.length) return null;
  const jaTem = new Set(produtosDoLead);
  const ordenados = [...roteiro].sort((a, b) => (a.ordem ?? 999) - (b.ordem ?? 999));
  return ordenados.find((r) => !jaTem.has(r.produto))?.produto ?? null;
}

/**
 * A data de renovação, um ano depois do início (`salvarVigenciaAcompanhamentoAnual`, `6071-6073`).
 *
 * As contas são as do legado, inclusive o `toISOString`, que devolve a data em UTC: o legado lê a
 * meia-noite LOCAL e escreve em UTC, e a mesma sequência aqui dá o mesmo texto que ele grava.
 */
export function dataDeRenovacao(dataInicio: string): string {
  const renovacao = new Date(`${dataInicio}T00:00:00`);
  renovacao.setFullYear(renovacao.getFullYear() + 1);
  return renovacao.toISOString().slice(0, 10);
}

/** O estado da vigência na tela: vencida, próxima dos 45 dias, ou em dia (`admin.html:6054-6059`). */
export type EstadoDaVigencia = 'vencida' | 'proxima' | 'em-dia';

/** Os dias até a renovação, contados de meia-noite a meia-noite, e o estado que a cor mostra. */
export function estadoDaVigencia(dataRenovacao: string | null, hoje: Date): { dias: number; estado: EstadoDaVigencia } {
  const renovacao = new Date(`${dataRenovacao}T00:00:00`);
  // Uma cópia de hoje zerada à meia-noite local, como `hoje.setHours(0, 0, 0, 0)`.
  const zero = new Date(hoje);
  zero.setHours(0, 0, 0, 0);
  // 86.400.000 ms é um dia; o arredondamento absorve a hora de verão.
  const dias = Math.round((renovacao.getTime() - zero.getTime()) / 86400000);
  return { dias, estado: dias < 0 ? 'vencida' : dias <= 45 ? 'proxima' : 'em-dia' };
}

/** `new Date(iso).toLocaleDateString('pt-BR')`, com a meia-noite local quando vem só a data. */
export function dataBrDeDia(dia: string | null): string {
  // Nulo vira "nullT00:00:00", data inválida, e o texto "Invalid Date" do legado.
  return new Date(`${dia}T00:00:00`).toLocaleDateString('pt-BR');
}

/** "Última atualização em dd/mm/aaaa, hh:mm:ss", como `toLocaleString('pt-BR')` do legado. */
export function textoUltimaAtualizacao(iso: string): string {
  return `Última atualização em ${new Date(iso).toLocaleString('pt-BR')}`;
}

/**
 * A máscara do telefone enquanto se digita no Questionário Geral (`admin.html:4847-4853`): até 11
 * dígitos, com DDD entre parênteses a partir do terceiro e hífen a partir do oitavo.
 */
export function mascararTelefoneDigitado(valor: string): string {
  const digitos = valor.replace(/\D/g, '').slice(0, 11);
  let formatado = digitos;
  if (digitos.length > 2) formatado = `(${digitos.slice(0, 2)}) ${digitos.slice(2)}`;
  if (digitos.length > 7) formatado = `(${digitos.slice(0, 2)}) ${digitos.slice(2, 7)}-${digitos.slice(7)}`;
  return formatado;
}
